<template>
    <app-layout :showTrialPeriodBar="false" :isPageLoading="isLoading" :isGlobalErrorHandlerUsed="true">
        <common-page-plain>
            <div class="mcard-share-root">
                <div v-if="!isLoading && invalidKind" class="mcard-share-message">
                    <h2>{{ $t(`pages.public.mcard_share.${invalidKind}_heading`) }}</h2>
                    <p>{{ $t(`pages.public.mcard_share.${invalidKind}_message`) }}</p>
                </div>

                <div v-else-if="!isLoading && loadError" class="mcard-share-message">
                    <p class="error">{{ loadError }}</p>
                </div>

                <div v-else-if="!isLoading && share" class="mcard-share">
                    <h2>{{ $t('pages.public.mcard_share.heading', { name: patientName }) }}</h2>
                    <p class="note">
                        {{ $t('pages.public.mcard_share.shared_note', { date: expiresOn }) }}
                    </p>

                    <section v-for="section in sections" :key="section.key" class="mcard-section">
                        <h3>{{ $t(`pages.public.mcard_share.sections.${section.label}`) }}</h3>
                        <ul>
                            <li v-for="(line, index) in section.lines" :key="index">{{ line }}</li>
                        </ul>
                    </section>

                    <p v-if="pdfError" class="error">{{ pdfError }}</p>
                    <common-buttons align="center">
                        <common-button
                            :label="$t('pages.public.mcard_share.download_pdf')"
                            type="primary"
                            :isProcessing="isDownloading"
                            :isDisabled="isDownloading"
                            @click="downloadPdf"
                        ></common-button>
                    </common-buttons>
                </div>
            </div>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import McardSharesAPI from 'api/McardSharesAPI'
    import { MCARD_SHARE_SECTIONS, mcardSectionLines } from '~/utils/mcardShare'

    export default {
        components: {
            AppLayout,
        },

        data() {
            return {
                isLoading: true,
                share: null,
                invalidKind: '',
                loadError: '',
                pdfError: '',
                isDownloading: false,
            }
        },

        computed: {
            token() {
                return this.$route.params.token
            },

            patientName() {
                const patient = (this.share && this.share.patient) || {}
                return `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
            },

            expiresOn() {
                if (!this.share || !this.share.expiresAt) return ''
                return new Date(this.share.expiresAt).toLocaleDateString()
            },

            sections() {
                const mcard = (this.share && this.share.mcard) || {}
                return MCARD_SHARE_SECTIONS.map((section) => ({
                    key: section.key,
                    label: section.label,
                    lines: mcardSectionLines(mcard[section.key]),
                })).filter((section) => section.lines.length > 0)
            },
        },

        methods: {
            downloadPdf() {
                if (this.isDownloading) return

                this.isDownloading = true
                this.pdfError = ''

                return McardSharesAPI.getPdf(this.token)
                    .then((blob) => {
                        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
                        const link = document.createElement('a')
                        link.href = url
                        link.download = 'mcard.pdf'
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                    })
                    .catch(() => {
                        this.pdfError = this.$t('pages.public.mcard_share.errors.pdf_failed')
                    })
                    .finally(() => {
                        this.isDownloading = false
                    })
            },
        },

        mounted() {
            McardSharesAPI.get(this.token)
                .then((res) => {
                    this.share = res
                })
                .catch((err) => {
                    const subCode = err?.response?.data?.subCode
                    if (subCode === 'token.expired') {
                        this.invalidKind = 'expired'
                    } else if (subCode === 'token.invalid') {
                        // Core answers the same for an unknown token and for one meant for another account.
                        this.invalidKind = 'not_found'
                    } else {
                        this.loadError = this.$t('pages.public.mcard_share.errors.load_failed')
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
    }
</script>

<style scoped lang="scss">
    .mcard-share-root {
        width: 100%;
    }

    .mcard-share-message {
        text-align: center;
        padding: 40px 20px;
    }

    .mcard-share {
        max-width: 640px;
        margin: 0 auto;
        padding: 24px 20px;

        h2 {
            text-align: center;
        }

        .note {
            text-align: center;
            margin-bottom: 24px;
            color: #6b7280;
        }
    }

    .mcard-section {
        margin-bottom: 20px;

        h3 {
            margin-bottom: 6px;
        }

        ul {
            margin: 0;
            padding-left: 20px;
        }
    }

    .error {
        color: #c62828;
        margin-bottom: 12px;
        font-size: 13px;
    }
</style>
